import { useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuth } from '../../context/AuthContext'
import { Logo } from '../ui/Logo'

type GroupKey = 'proyectos' | 'finanzas' | 'modulos' | null

const MODULES = [
  { to: '/modules/idiomas', icon: '🌐', label: 'Idiomas' },
  { to: '/modules/gimnasio', icon: '🏋️', label: 'Gimnasio' },
  { to: '/modules/habitos', icon: '🔁', label: 'Hábitos' },
]

const FINANCE_SUBMENU = [
  { to: '/finance/movements', icon: '📄', label: 'Movimientos' },
  { to: '/finance/budget', icon: '🎯', label: 'Presupuesto' },
  { to: '/finance/cards', icon: '💳', label: 'Tarjetas' },
  { to: '/finance/accounts', icon: '🏦', label: 'Cuentas' },
  { to: '/finance/investments', icon: '📈', label: 'Inversiones' },
]

const PROJECTS_SUBMENU = [
  { to: '/calendar', icon: '📅', label: 'Calendario' },
  { to: '/ideas', icon: '💡', label: 'Ideas' },
]

function groupForPath(pathname: string): GroupKey {
  if (pathname === '/projects' || pathname === '/calendar' || pathname === '/ideas') return 'proyectos'
  if (pathname.startsWith('/finance')) return 'finanzas'
  if (pathname.startsWith('/modules')) return 'modulos'
  return null
}

function navLinkClass({ isActive }: { isActive: boolean }) {
  return clsx(
    'flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 font-semibold text-muted transition-colors hover:bg-panel-2 hover:text-text',
    isActive && 'bg-panel-2 text-accent',
  )
}

function subNavLinkClass({ isActive }: { isActive: boolean }) {
  return clsx(
    'flex items-center gap-2.5 rounded-[10px] py-2 pr-3 pl-8 text-sm font-medium text-muted transition-colors hover:bg-panel-2 hover:text-text',
    isActive && 'bg-panel-2 text-accent',
  )
}

/** Fila dentro de un menu flotante (mini-sidebar). */
function FlyoutLink({ to, icon, label, onNavigate }: { to: string; icon: string; label: string; onNavigate: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-muted transition-colors hover:bg-panel-2 hover:text-text',
          isActive && 'bg-panel-2 text-accent',
        )
      }
    >
      <span>{icon}</span> {label}
    </NavLink>
  )
}

/** Menu flotante que aparece al pasar el mouse sobre un icono en mini-sidebar -- no
 * empuja ni agranda el riel, es un panel superpuesto (position absolute). */
function Flyout({ children }: { children: ReactNode }) {
  return (
    <div className="pointer-events-none absolute top-0 left-full z-50 ml-2 min-w-[190px] scale-95 rounded-xl border border-line bg-panel/95 p-1.5 opacity-0 shadow-2xl shadow-black/40 backdrop-blur-xl transition-all duration-150 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100">
      {children}
    </div>
  )
}

/** Un icono de mini-sidebar con su etiqueta en un tooltip/flyout al pasar el mouse. */
function MiniItem({
  to,
  icon,
  label,
  isActive,
  children,
  onNavigate,
}: {
  to?: string
  icon: string
  label: string
  isActive?: boolean
  children?: ReactNode
  onNavigate: () => void
}) {
  const iconClass = clsx(
    'flex h-11 w-11 items-center justify-center rounded-[10px] text-lg text-muted transition-colors hover:bg-panel-2 hover:text-text',
    isActive && 'bg-panel-2 text-accent',
  )

  return (
    <div className="group relative flex justify-center">
      {to ? (
        <NavLink to={to} onClick={onNavigate} className={iconClass} aria-label={label} title={label}>
          {icon}
        </NavLink>
      ) : (
        <span className={iconClass} aria-label={label}>
          {icon}
        </span>
      )}
      <Flyout>
        <div className="px-2.5 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">{label}</div>
        {children}
      </Flyout>
    </div>
  )
}

interface SidebarProps {
  mobileOpen: boolean
  collapsed: boolean
  onNavigate: () => void
}

export function Sidebar({ mobileOpen, collapsed, onNavigate }: SidebarProps) {
  const { isAdmin } = useAuth()
  const location = useLocation()
  const [openGroup, setOpenGroup] = useState<GroupKey>(() => groupForPath(location.pathname))
  const [prevPathname, setPrevPathname] = useState(location.pathname)

  // Ajusta el grupo abierto cuando cambia la ruta (p.ej. navegacion directa por URL),
  // sin useEffect: React recomienda ajustar estado derivado durante el render.
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname)
    const active = groupForPath(location.pathname)
    if (active) setOpenGroup(active)
  }

  return (
    <aside
      className={clsx(
        'fixed inset-y-0 left-0 z-40 flex h-screen w-60 flex-shrink-0 flex-col overflow-visible border-r border-line/40 bg-panel/35 backdrop-blur-xl backdrop-saturate-150 transition-all duration-200',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0',
        collapsed ? 'md:w-[4.5rem]' : 'md:w-60',
        collapsed ? 'p-3' : 'p-5 px-3.5',
      )}
    >
      {collapsed ? (
        <div className="flex justify-center pb-5">
          <Logo size={26} />
        </div>
      ) : (
        <div className="flex items-center gap-2 px-2 pb-5 text-2xl font-extrabold">
          <Logo size={26} />
          ne<span className="text-accent">x</span>o
        </div>
      )}

      {collapsed ? (
        <nav className="flex flex-1 flex-col items-center gap-1.5 overflow-visible">
          <MiniItem
            to="/dashboard"
            icon="▦"
            label="Dashboard"
            isActive={location.pathname === '/dashboard'}
            onNavigate={onNavigate}
          />
          <MiniItem to="/projects" icon="▤" label="Proyectos" isActive={groupForPath(location.pathname) === 'proyectos'} onNavigate={onNavigate}>
            {PROJECTS_SUBMENU.map((item) => (
              <FlyoutLink key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </MiniItem>
          <MiniItem to="/finance" icon="💰" label="Finanzas" isActive={groupForPath(location.pathname) === 'finanzas'} onNavigate={onNavigate}>
            {FINANCE_SUBMENU.map((item) => (
              <FlyoutLink key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </MiniItem>
          <MiniItem icon="🧩" label="Módulos" onNavigate={onNavigate}>
            {MODULES.map((item) => (
              <FlyoutLink key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </MiniItem>
          {isAdmin && (
            <>
              <div className="my-2 h-px w-8 bg-line" />
              <MiniItem to="/admin" icon="🔐" label="Seguridad" isActive={location.pathname === '/admin'} onNavigate={onNavigate} />
            </>
          )}
        </nav>
      ) : (
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          <NavLink to="/dashboard" className={navLinkClass} onClick={onNavigate}>
            <span className="w-4.5 text-center">▦</span>
            Dashboard
          </NavLink>
          <NavLink
            to="/projects"
            className={navLinkClass}
            onClick={() => {
              setOpenGroup('proyectos')
              onNavigate()
            }}
          >
            <span className="w-4.5 text-center">▤</span>
            Proyectos
            <span className="ml-auto text-xs text-muted">{openGroup === 'proyectos' ? '▾' : '▸'}</span>
          </NavLink>
          {openGroup === 'proyectos' &&
            PROJECTS_SUBMENU.map((item) => (
              <NavLink key={item.to} to={item.to} className={subNavLinkClass} onClick={onNavigate}>
                {item.icon} {item.label}
              </NavLink>
            ))}

          <NavLink
            to="/finance"
            className={navLinkClass}
            onClick={() => {
              setOpenGroup('finanzas')
              onNavigate()
            }}
          >
            <span className="w-4.5 text-center">💰</span>
            Finanzas
            <span className="ml-auto text-xs text-muted">{openGroup === 'finanzas' ? '▾' : '▸'}</span>
          </NavLink>
          {openGroup === 'finanzas' &&
            FINANCE_SUBMENU.map((item) => (
              <NavLink key={item.to} to={item.to} className={subNavLinkClass} onClick={onNavigate}>
                <span>{item.icon}</span> {item.label}
              </NavLink>
            ))}

          <button
            type="button"
            className={clsx(
              'flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 font-semibold text-muted transition-colors hover:bg-panel-2 hover:text-text',
              openGroup === 'modulos' && 'text-text',
            )}
            onClick={() => setOpenGroup('modulos')}
            aria-expanded={openGroup === 'modulos'}
          >
            <span className="w-4.5 text-center">🧩</span>
            Módulos
            <span className="ml-auto text-xs text-muted">{openGroup === 'modulos' ? '▾' : '▸'}</span>
          </button>
          {openGroup === 'modulos' &&
            MODULES.map((item) => (
              <NavLink key={item.to} to={item.to} className={subNavLinkClass} onClick={onNavigate}>
                <span>{item.icon}</span> {item.label}
              </NavLink>
            ))}

          {isAdmin && (
            <>
              <div className="px-3 pt-3.5 pb-1 text-[11px] font-semibold tracking-wide text-muted uppercase">
                Administración
              </div>
              <NavLink to="/admin" className={navLinkClass} onClick={onNavigate}>
                <span className="w-4.5 text-center">🔐</span>
                Seguridad
              </NavLink>
            </>
          )}
        </nav>
      )}
    </aside>
  )
}
